import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import PageTitle from "../../PageTitle";
import useAxios from "../../../utilities/useAxios";
import {
	Table,
	Button,
	message,
	Tag,
	Space,
	Tabs,
	Layout,
	Switch,
	Select,
	Modal,
	Form,
	DatePicker,
	TimePicker,
	Input,
	Tooltip,
	Alert,
	Badge,
	Checkbox,
	Radio,
	Upload,
	Spin,
	Segmented,
	Dropdown,
} from "antd";
import {
	PlusOutlined,
	MoreOutlined,
	UserOutlined,
	SearchOutlined,
	UploadOutlined,
	DeleteOutlined,
	DownloadOutlined,
	EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import { ConfigProvider } from "antd";
import PageHeader from "../../common/PageHeader";
import ApprovalSteps, {
	deriveApprovalState,
	approvalStateLabel,
} from "./ApprovalSteps";
import useIsMobile from "../../../hooks/useIsMobile";
import LeaveManageNavigation from "./navigation/LeaveManageNavigation";
import LeaveMultiDatePicker from "./LeaveMultiDatePicker";
import { EXCUSE_REASON_CATEGORIES } from "./leaveConstants";

dayjs.locale("ko");

/** 관리자 기간형 휴가 유형 */
const ADMIN_SUBTAB_VACATION_TYPES = [
	"연차",
	"오전반차",
	"오후반차",
	"경조사",
	"병가",
	"특별휴가",
	"기타",
];

const ANNUAL_HALF_TYPES = ["연차", "오전반차", "오후반차", "월차"];
const FAMILY_LEAVE_TYPES = ["경조사"];
const SICK_LEAVE_TYPES = ["병가"];
const TRIP_TYPES = ["출장", "시내출장"];
const MENSTRUAL_TYPES = ["생휴"];
const REPORT_ONLY_TYPES = new Set(["시내출장", "생휴", "사유서"]);

const isDateRangeLeaveType = (t) =>
	t === "출장" ||
	["연차", "경조사", "병가", "특별휴가", "기타"].includes(t);

/** 신청 수정 폼 등: 사유서 포함 구간형 표현 */
const usesStartEndDates = (t) => isDateRangeLeaveType(t) || t === "사유서";

/** 관리자 근태 신청 등록에서 선택 가능한 전체 구분 */
const ADMIN_APPLY_LEAVE_OPTIONS = [
	{ value: "연차", label: "연차" },
	{ value: "오전반차", label: "오전 반차" },
	{ value: "오후반차", label: "오후 반차" },
	{ value: "월차", label: "월차" },
	{ value: "사유서", label: "사유서" },
	{ value: "출장", label: "출장" },
	{ value: "시내출장", label: "시내출장" },
	{ value: "경조사", label: "경조사" },
	{ value: "병가", label: "병가" },
	{ value: "생휴", label: "생리휴가" },
	{ value: "특별휴가", label: "특별휴가" },
	{ value: "기타", label: "기타" },
];

const APPLY_SINGLE_DATE_TYPES = ["오전반차", "오후반차", "월차", "생휴"];
const APPLY_PERIOD_TYPES = ["연차", "경조사", "병가", "특별휴가", "기타"];

/** 대리 등록 시 결재선을 지정하는 구분. 월차는 함께 만들어지는 연차가 결재를 탄다. */
const APPROVAL_APPLY_TYPES = new Set([
	"연차",
	"오전반차",
	"오후반차",
	"월차",
	"출장",
	"경조사",
	"병가",
]);

const ApplyApprovalLineInput = ({
	leaveType,
	applicantId,
	loading,
	line,
	mode,
	receivedOrders,
	onModeChange,
	onReceivedChange,
}) => {
	if (!APPROVAL_APPLY_TYPES.has(leaveType)) return null;

	const steps = line?.steps || [];
	const toggleReceived = (order, checked) => {
		if (checked) {
			onReceivedChange(
				receivedOrders.includes(order)
					? receivedOrders
					: [...receivedOrders, order],
			);
			return;
		}
		onReceivedChange(
			receivedOrders.filter((item) => item !== order),
		);
	};

	return (
		<Form.Item
			label="결재 처리"
			required
			extra={
				mode === "partial"
					? "체크한 단계는 결재 완료로 등록하고, 나머지 단계는 순서대로 결재를 요청합니다."
					: "아래 결재라인을 처음부터 순서대로 진행합니다."
			}>
			{leaveType === "월차" && (
				<Alert
					type="info"
					showIcon
					style={{ marginBottom: 12 }}
					message="월차와 함께 같은 날 연차가 등록됩니다. 아래 결재는 그 연차에 적용됩니다."
				/>
			)}
			{!applicantId && (
				<Alert
					type="info"
					showIcon
					message="신청자를 선택하면 해당 인원의 결재라인이 표시됩니다."
				/>
			)}
			{applicantId && loading && (
				<span>결재라인을 확인하는 중입니다.</span>
			)}
			{applicantId && !loading && !line && (
				<Alert
					type="error"
					showIcon
					message="결재라인을 불러오지 못했습니다. 신청자를 다시 선택해주세요."
				/>
			)}
			{applicantId && !loading && line && (
				<>
					{line.uses_route ? (
						<div
							style={{
								marginBottom: 8,
							}}>
							적용 결재라인:{" "}
							<strong>
								{
									line.route_name
								}
							</strong>
						</div>
					) : (
						<Alert
							type="info"
							showIcon
							style={{
								marginBottom: 8,
							}}
							message="지정된 결재 단계가 없어 팀 리더, 사무국장, 당회장 순서로 진행됩니다."
						/>
					)}
					<Radio.Group
						value={mode}
						onChange={(event) =>
							onModeChange(
								event.target
									.value,
							)
						}>
						<Space direction="vertical">
							<Radio value="full">
								결재라인을 모두
								진행
							</Radio>
							{steps.some(
								(step) =>
									!step.auto_approved,
							) && (
								<Radio value="partial">
									이미
									받은
									결재가
									있음
								</Radio>
							)}
						</Space>
					</Radio.Group>
					<div style={{ marginTop: 12 }}>
						{steps.map((step) => {
							const who =
								step.approver_name
									? `${step.label} · ${step.approver_name}`
									: step.label;
							if (
								mode !==
								"partial"
							) {
								return (
									<div
										key={
											step.order
										}
										style={{
											marginBottom: 4,
										}}>
										{
											who
										}
										{step.auto_approved
											? " (본인이 팀 리더라 자동 승인)"
											: ""}
									</div>
								);
							}
							return (
								<div
									key={
										step.order
									}>
									<Checkbox
										checked={
											step.auto_approved ||
											receivedOrders.includes(
												step.order,
											)
										}
										disabled={
											step.auto_approved
										}
										onChange={(
											event,
										) =>
											toggleReceived(
												step.order,
												event
													.target
													.checked,
											)
										}>
										{
											who
										}
										{step.auto_approved
											? " — 본인이 팀 리더라 자동 승인"
											: " — 이미 결재받음"}
									</Checkbox>
								</div>
							);
						})}
					</div>
				</>
			)}
		</Form.Item>
	);
};

const tabForApplyLeaveType = (leaveType) => {
	if (["연차", "오전반차", "오후반차", "월차"].includes(leaveType)) {
		return "연차(반차)";
	}
	if (leaveType === "사유서") return "사유서";
	if (leaveType === "출장" || leaveType === "시내출장") return "출장";
	if (leaveType === "경조사") return "경조휴가";
	if (leaveType === "병가") return "병가";
	if (leaveType === "생휴") return "생리휴가";
	return null;
};

const countWeekdays = (start, end) => {
	if (!start || !end || end.isBefore(start, "day")) return 0;
	let count = 0;
	let cursor = start.startOf("day");
	const last = end.startOf("day");
	while (!cursor.isAfter(last, "day")) {
		if (cursor.day() !== 0) count += 1;
		cursor = cursor.add(1, "day");
	}
	return count;
};

const applyEndDateRules = (leaveType) => [
	{
		required: true,
		message: "종료일을 선택해주세요",
	},
	({ getFieldValue }) => ({
		validator(_, value) {
			const start = getFieldValue("start_date");
			if (!value || !start) return Promise.resolve();
			if (value.isBefore(start, "day")) {
				return Promise.reject(
					new Error(
						"종료일은 시작일 이후여야 합니다.",
					),
				);
			}
			if (
				leaveType === "경조사" &&
				countWeekdays(start, value) > 5
			) {
				return Promise.reject(
					new Error(
						"경조휴가는 최대 5일(일요일 제외)까지 사용할 수 있습니다.",
					),
				);
			}
			return Promise.resolve();
		},
	}),
];

const sortDayjsDates = (dates) =>
	[...(dates || [])]
		.filter(Boolean)
		.sort((a, b) => a.valueOf() - b.valueOf());

/** 미사용 공휴일 반차 중 사용일이 사용 가능 기간에 들어가는 개수 */
const countHolidayHalfUsableOnDate = (credits, leaveDate) => {
	if (!leaveDate) return 0;
	const day = dayjs(leaveDate).startOf("day");
	return (credits || []).filter((credit) => {
		if (credit.is_used) return false;
		const from = dayjs(credit.usable_from).startOf("day");
		const until = dayjs(credit.expires_on).startOf("day");
		if (!from.isValid() || !until.isValid()) return false;
		return !day.isBefore(from, "day") && !day.isAfter(until, "day");
	}).length;
};

const countHolidayHalfRemaining = (credits) => {
	const today = dayjs().startOf("day");
	return (credits || []).filter((credit) => {
		if (credit.is_used) return false;
		const until = dayjs(credit.expires_on).startOf("day");
		return until.isValid() && !until.isBefore(today, "day");
	}).length;
};

/** API TimeField 값("HH:mm:ss")을 TimePicker용 dayjs로 변환 */
const parseApiTime = (value) => {
	if (!value) return undefined;
	const match = String(value).match(/^(\d{2}:\d{2})(?::(\d{2}))?/);
	if (!match) return undefined;
	const parsed = dayjs(`2000-01-01T${match[1]}:${match[2] || "00"}`);
	return parsed.isValid() ? parsed : undefined;
};

const firstApiErrorMessage = (data) => {
	if (!data) return null;
	if (typeof data === "string" && data.trim()) return data;
	if (typeof data.error === "string" && data.error.trim()) {
		return data.error;
	}
	if (typeof data !== "object") return null;
	for (const value of Object.values(data)) {
		if (Array.isArray(value) && value.length) {
			const item = value[0];
			if (typeof item === "string" && item.trim())
				return item;
		}
		if (typeof value === "string" && value.trim()) return value;
	}
	return null;
};

const renderLeavePeriodCell = (_, record) => {
	if (isDateRangeLeaveType(record.leave_type)) {
		if (record.start_date && record.end_date) {
			const s = dayjs(record.start_date);
			const e = dayjs(record.end_date);
			if (s.isSame(e, "day")) {
				return s.format("YYYY-MM-DD");
			}
			return `${s.format("YYYY-MM-DD")} ~ ${e.format("YYYY-MM-DD")}`;
		}
		return record.leave_date
			? dayjs(record.leave_date).format("YYYY-MM-DD")
			: "—";
	}
	if (
		record.leave_type === "사유서" &&
		record.start_date &&
		record.end_date
	) {
		const s = dayjs(record.start_date);
		const e = dayjs(record.end_date);
		if (s.isSame(e, "day")) {
			return s.format("YYYY-MM-DD");
		}
		return `${s.format("YYYY-MM-DD")} ~ ${e.format("YYYY-MM-DD")}`;
	}
	return dayjs(record.leave_date).format("YYYY-MM-DD");
};

const { Content } = Layout;
const { Option } = Select;
const { TextArea } = Input;

const LEAVE_ADMIN_TABLE_SCROLL_X = 1280;

const isViewableProofAttachment = (filename) =>
	/\.(pdf|jpe?g|png|gif|webp|bmp)$/i.test(filename || "");

const proofAttachmentMime = (filename) => {
	const ext = (filename || "").split(".").pop()?.toLowerCase();
	const map = {
		pdf: "application/pdf",
		jpg: "image/jpeg",
		jpeg: "image/jpeg",
		png: "image/png",
		gif: "image/gif",
		webp: "image/webp",
		bmp: "image/bmp",
	};
	return map[ext] || "application/octet-stream";
};

const ViewLeaveApplications = ({ hideNavigation = false }) => {
	const location = useLocation();
	const [applications, setApplications] = useState([]);
	const [loading, setLoading] = useState(false);
	const [togglingAppliedId, setTogglingAppliedId] = useState(null);
	const [activeTab, setActiveTab] = useState("연차(반차)");
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [jobFilter, setJobFilter] = useState("전체");
	const [approvalStatusFilter, setApprovalStatusFilter] =
		useState("all");
	const isMobile = useIsMobile();
	const [monthlyPage, setMonthlyPage] = useState(1);
	const [generalPage, setGeneralPage] = useState(1);
	const [tripPage, setTripPage] = useState(1);
	const [vacationPage, setVacationPage] = useState(1);
	const [familyPage, setFamilyPage] = useState(1);
	const [sickPage, setSickPage] = useState(1);
	const [menstrualPage, setMenstrualPage] = useState(1);
	const [applyModalOpen, setApplyModalOpen] = useState(false);
	const [applySubmitting, setApplySubmitting] = useState(false);
	const [gateDutyUploading, setGateDutyUploading] = useState(false);
	const [proofPreview, setProofPreview] = useState(null);
	const [proofPreviewLoading, setProofPreviewLoading] =
		useState(false);
	const gateDutyInputRef = useRef(null);
	const [applyForm] = Form.useForm();
	const [editForm] = Form.useForm();
	const applyLocalReturnType = Form.useWatch(
		"local_trip_return_type",
		applyForm,
	);
	const applyApplicantId = Form.useWatch("applicant_id", applyForm);
	const applyLeaveDate = Form.useWatch("leave_date", applyForm);
	const editLocalReturnType = Form.useWatch(
		"local_trip_return_type",
		editForm,
	);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editingRecord, setEditingRecord] = useState(null);
	const [editLeaveType, setEditLeaveType] = useState(null);
	const [additionalUsersContext, setAdditionalUsersContext] =
		useState("apply");
	const [cancelForm] = Form.useForm();
	const [applyLeaveType, setApplyLeaveType] = useState(null);
	const [applyUseHolidayHalf, setApplyUseHolidayHalf] = useState(false);
	const [applyHolidayCredits, setApplyHolidayCredits] = useState([]);
	const [applyApprovalMode, setApplyApprovalMode] = useState("full");
	const [applyApprovalReceived, setApplyApprovalReceived] = useState([]);
	const [applyApprovalLine, setApplyApprovalLine] = useState(null);
	const [applyApprovalLineLoading, setApplyApprovalLineLoading] =
		useState(false);
	const [cancelModalOpen, setCancelModalOpen] = useState(false);
	const [cancellingRecord, setCancellingRecord] = useState(null);
	const [approvalEditOpen, setApprovalEditOpen] = useState(false);
	const [approvalEditRecord, setApprovalEditRecord] = useState(null);
	const [approvalEditSaving, setApprovalEditSaving] = useState(false);
	const [approvalEditForm] = Form.useForm();
	const [clergyStaffList, setClergyStaffList] = useState([]);
	const [additionalUsersModalVisible, setAdditionalUsersModalVisible] =
		useState(false);
	const [availableUsers, setAvailableUsers] = useState([]);
	const [selectedAdditionalUsers, setSelectedAdditionalUsers] = useState(
		[],
	);
	const [searchText, setSearchText] = useState("");
	const [nameSearch, setNameSearch] = useState("");
	const [filteredUsers, setFilteredUsers] = useState([]);

	useEffect(() => {
		const preset = location.state?.nameSearch;
		if (typeof preset === "string" && preset.trim()) {
			setNameSearch(preset.trim());
		}
	}, [location.state]);
	const [isLeaveManageCompact, setIsLeaveManageCompact] = useState(
		() =>
			typeof window !== "undefined" &&
			window.matchMedia("(max-width: 576px)").matches,
	);

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}
		const mq = window.matchMedia("(max-width: 576px)");
		const sync = () => setIsLeaveManageCompact(mq.matches);
		sync();
		mq.addEventListener("change", sync);
		return () => mq.removeEventListener("change", sync);
	}, []);

	const leaveAdminTableProps = useMemo(
		() => ({
			scroll: {
				x: LEAVE_ADMIN_TABLE_SCROLL_X,
			},
			tableLayout: "fixed",
			sticky: { offsetHeader: 64 },
			style: {
				width: "100%",
			},
		}),
		[],
	);

	useEffect(() => {
		fetchApplications();
	}, []);

	useEffect(() => {
		return () => {
			if (proofPreview?.url) {
				window.URL.revokeObjectURL(proofPreview.url);
			}
		};
	}, [proofPreview?.url]);

	const fetchProofAttachmentBlobUrl = async (
		applicationId,
		attachment,
	) => {
		const res = await useAxios.get(
			`/utils/leave/applications/${applicationId}/proof-attachments/${attachment.id}/`,
			{
				responseType: "blob",
				params: { inline: 1 },
				headers: {
					Accept: "application/octet-stream",
				},
			},
		);
		const mime = proofAttachmentMime(
			attachment.original_filename,
		);
		const blob = new Blob([res.data], { type: mime });
		return window.URL.createObjectURL(blob);
	};

	const closeProofPreview = () => {
		setProofPreview((prev) => {
			if (prev?.url) {
				window.URL.revokeObjectURL(prev.url);
			}
			return null;
		});
	};

	const openProofPreview = async (applicationId, attachment) => {
		setProofPreviewLoading(true);
		setProofPreview(null);
		try {
			const url = await fetchProofAttachmentBlobUrl(
				applicationId,
				attachment,
			);
			const mime = proofAttachmentMime(
				attachment.original_filename,
			);
			setProofPreview({
				url,
				title:
					attachment.original_filename || "미리보기",
				kind: mime === "application/pdf" ? "pdf" : "image",
			});
		} catch (error) {
			message.error("증빙자료 미리보기에 실패했습니다.");
			console.error(error);
		} finally {
			setProofPreviewLoading(false);
		}
	};

	const summarizeGateDutyIssues = (items) =>
		(items || [])
			.slice(0, 8)
			.map((item) => {
				const where = item.row ? `${item.row}행` : "";
				const who = [item.name, item.date]
					.filter(Boolean)
					.join(" ");
				return [where, who, item.message]
					.filter(Boolean)
					.join(" · ");
			})
			.join("\n");

	const handleGateDutyFile = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) {
			return;
		}
		if (
			!String(file.name || "")
				.toLowerCase()
				.endsWith(".xlsx")
		) {
			message.error(
				".xlsx 엑셀 파일만 업로드할 수 있습니다.",
			);
			return;
		}
		const formData = new FormData();
		formData.append("file", file);
		setGateDutyUploading(true);
		try {
			const response = await useAxios.post(
				"/utils/leave/gate-duty-upload/",
				formData,
				{
					transformRequest: [
						(data, headers) => {
							delete headers[
								"Content-Type"
							];
							return data;
						},
					],
				},
			);
			const data = response.data || {};
			const created = data.created_count || 0;
			const issueText = [
				summarizeGateDutyIssues(data.errors),
				summarizeGateDutyIssues(data.skipped),
			]
				.filter(Boolean)
				.join("\n");
			if (created > 0) {
				message.success(
					`사유서 ${created}건을 등록했습니다. (경건회 불참 / 방비 - 정문 대기)`,
				);
				setActiveTab("사유서");
				fetchApplications();
			}
			if (issueText) {
				Modal.warning({
					title: created
						? "일부 행은 등록하지 않았습니다"
						: "등록된 사유서가 없습니다",
					content: (
						<pre
							style={{
								whiteSpace: "pre-wrap",
								margin: 0,
							}}>
							{issueText}
						</pre>
					),
				});
			} else if (!created) {
				message.info("등록할 일정이 없습니다.");
			}
		} catch (error) {
			const data = error.response?.data;
			const issueText = data
				? [
						summarizeGateDutyIssues(
							data.errors,
						),
						summarizeGateDutyIssues(
							data.skipped,
						),
					]
						.filter(Boolean)
						.join("\n")
				: "";
			if (issueText) {
				Modal.error({
					title: "방비 일정을 등록하지 못했습니다",
					content: (
						<pre
							style={{
								whiteSpace: "pre-wrap",
								margin: 0,
							}}>
							{issueText}
						</pre>
					),
				});
			} else {
				message.error(
					data?.error ||
						"방비 일정 업로드에 실패했습니다.",
				);
			}
		} finally {
			setGateDutyUploading(false);
		}
	};

	const fetchApplications = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/utils/leave/applications/",
			);
			if (response.status === 200) {
				setApplications(response.data);
			}
		} catch (error) {
			message.error("신청 목록을 불러오는데 실패했습니다.");
			console.error("에러:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleToggleApplied = async (record, checked) => {
		try {
			setTogglingAppliedId(record.id);
			const response = await useAxios.put(
				`/utils/leave/applications/${record.id}/`,
				{
					is_applied: checked,
				},
			);

			if (response.status === 200) {
				const updated = response.data;
				setApplications((prev) =>
					prev.map((app) =>
						app.id === record.id
							? { ...app, ...updated }
							: app,
					),
				);
				message.success(
					checked
						? "적용 완료로 표시되었습니다."
						: "미적용으로 변경되었습니다.",
				);
			}
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"상태 변경 중 오류가 발생했습니다.",
			);
			console.error("에러:", error);
		} finally {
			setTogglingAppliedId(null);
		}
	};

	const handleBulkUpdate = async (isApplied) => {
		if (selectedRowKeys.length === 0) {
			message.warning("선택된 항목이 없습니다.");
			return;
		}

		try {
			setLoading(true);
			const response = await useAxios.post(
				"/utils/leave/applications/bulk-update/",
				{
					application_ids: selectedRowKeys,
					is_applied: isApplied,
				},
			);

			if (response.status === 200) {
				const updatedList =
					response.data.applications || [];
				const byId = new Map(
					updatedList.map((app) => [app.id, app]),
				);
				setApplications((prev) =>
					prev.map((app) => {
						const updated = byId.get(
							app.id,
						);
						return updated
							? { ...app, ...updated }
							: app;
					}),
				);
				message.success(
					`${response.data.updated_count}건이 ${isApplied ? "적용" : "미적용"}으로 변경되었습니다.`,
				);
				setSelectedRowKeys([]);
			}
		} catch (error) {
			message.error("일괄 변경 중 오류가 발생했습니다.");
			console.error("에러:", error);
		} finally {
			setLoading(false);
		}
	};

	const fetchClergyStaff = async () => {
		try {
			const response = await useAxios.get(
				"/users/clergy-staff/",
			);
			if (response.status === 200) {
				setClergyStaffList(response.data);
			}
		} catch (error) {
			message.error(
				"교역자·교직원 목록을 불러오는데 실패했습니다.",
			);
			console.error(error);
		}
	};

	/** @param {string} contextTab */
	const openApplyModal = (contextTab) => {
		setAdditionalUsersContext("apply");
		applyForm.resetFields();
		setSelectedAdditionalUsers([]);
		setSearchText("");
		const tab = contextTab || activeTab || "연차(반차)";
		const defaultType =
			tab === "경조휴가"
				? "경조사"
				: tab === "병가"
					? "병가"
					: tab === "사유서"
						? "사유서"
						: tab === "출장"
							? "출장"
							: tab === "생리휴가"
								? "생휴"
								: "연차";
		setApplyLeaveType(defaultType);
		setApplyUseHolidayHalf(false);
		setApplyHolidayCredits([]);
		setApplyApprovalMode("full");
		setApplyApprovalReceived([]);
		setApplyApprovalLine(null);
		applyForm.setFieldsValue({
			leave_type: defaultType,
			additional_users: [],
			leave_date: undefined,
			start_date: undefined,
			end_date: undefined,
			leave_dates: [],
		});
		setApplyModalOpen(true);
		fetchClergyStaff();
	};

	const applyDisabledDate = (current) =>
		Boolean(current && current.day() === 0);

	const clearApplyTypeFields = () => {
		applyForm.setFieldsValue({
			leave_date: undefined,
			start_date: undefined,
			end_date: undefined,
			leave_dates: [],
			reason: undefined,
			reason_category: undefined,
			additional_users: [],
			trip_title: undefined,
			trip_background: undefined,
			trip_location: undefined,
			trip_includes_lodging: false,
			trip_transport: undefined,
			trip_note: undefined,
			local_trip_departure_time: undefined,
			local_trip_return_type: undefined,
			local_trip_return_time: undefined,
		});
		setSelectedAdditionalUsers([]);
	};

	const handleApplyLeaveTypeChange = (value) => {
		if (value === "생휴") {
			const person = clergyStaffList.find(
				(u) =>
					u.id ===
					applyForm.getFieldValue("applicant_id"),
			);
			if (person && person.gender !== "여") {
				message.warning(
					"생리휴가는 여성 신청자만 등록할 수 있습니다.",
				);
				applyForm.setFieldsValue({
					leave_type: undefined,
				});
				setApplyLeaveType(null);
				clearApplyTypeFields();
				return;
			}
		}
		setApplyLeaveType(value);
		setApplyUseHolidayHalf(false);
		clearApplyTypeFields();
	};

	const handleApplyApplicantChange = (applicantId) => {
		const person = clergyStaffList.find(
			(u) => u.id === applicantId,
		);
		if (
			person &&
			person.gender !== "여" &&
			applyForm.getFieldValue("leave_type") === "생휴"
		) {
			message.warning(
				"생리휴가는 여성 신청자만 등록할 수 있습니다.",
			);
			applyForm.setFieldsValue({ leave_type: undefined });
			setApplyLeaveType(null);
		}
		setApplyUseHolidayHalf(false);
	};

	useEffect(() => {
		if (!applyModalOpen || !applyApplicantId) {
			setApplyHolidayCredits([]);
			return undefined;
		}
		let cancelled = false;
		(async () => {
			try {
				const response = await useAxios.get(
					"/utils/leave/holiday-half-day/",
					{
						params: {
							user_id: applyApplicantId,
						},
					},
				);
				if (!cancelled) {
					setApplyHolidayCredits(
						Array.isArray(response.data)
							? response.data
							: [],
					);
				}
			} catch {
				if (!cancelled) setApplyHolidayCredits([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [applyModalOpen, applyApplicantId]);

	const applyHolidayHalfRemaining = useMemo(
		() => countHolidayHalfRemaining(applyHolidayCredits),
		[applyHolidayCredits],
	);
	const applyHolidayHalfUsableOnDate = useMemo(
		() =>
			countHolidayHalfUsableOnDate(
				applyHolidayCredits,
				applyLeaveDate,
			),
		[applyHolidayCredits, applyLeaveDate],
	);
	const showApplyHolidayHalf =
		applyLeaveType === "오전반차" || applyLeaveType === "오후반차";

	useEffect(() => {
		if (applyHolidayHalfUsableOnDate < 1 && applyUseHolidayHalf) {
			setApplyUseHolidayHalf(false);
		}
	}, [applyHolidayHalfUsableOnDate, applyUseHolidayHalf]);

	useEffect(() => {
		if (
			!applyModalOpen ||
			!applyApplicantId ||
			!APPROVAL_APPLY_TYPES.has(applyLeaveType)
		) {
			setApplyApprovalLine(null);
			setApplyApprovalLineLoading(false);
			setApplyApprovalMode("full");
			setApplyApprovalReceived([]);
			return undefined;
		}
		let cancelled = false;
		(async () => {
			setApplyApprovalLineLoading(true);
			setApplyApprovalMode("full");
			setApplyApprovalReceived([]);
			try {
				const response = await useAxios.get(
					"/utils/leave/approval-line-preview/",
					{
						params: {
							user_id: applyApplicantId,
						},
					},
				);
				if (!cancelled) {
					setApplyApprovalLine(response.data);
				}
			} catch {
				if (!cancelled) setApplyApprovalLine(null);
			} finally {
				if (!cancelled)
					setApplyApprovalLineLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [applyModalOpen, applyApplicantId, applyLeaveType]);

	const handleApplySubmit = async (values) => {
		if (values.leave_type === "생휴") {
			const person = clergyStaffList.find(
				(u) => u.id === values.applicant_id,
			);
			if (person?.gender !== "여") {
				message.error(
					"생리휴가는 여성 신청자만 등록할 수 있습니다.",
				);
				return;
			}
		}
		if (APPROVAL_APPLY_TYPES.has(values.leave_type)) {
			if (applyApprovalLineLoading || !applyApprovalLine) {
				message.error(
					"결재라인을 확인한 뒤 등록해주세요.",
				);
				return;
			}
			if (applyApprovalMode === "partial") {
				const choosable = (
					applyApprovalLine.steps || []
				).filter((step) => !step.auto_approved);
				if (
					choosable.length > 0 &&
					applyApprovalReceived.length === 0
				) {
					message.error(
						"이미 받은 결재 단계를 하나 이상 선택해주세요.",
					);
					return;
				}
			}
		}

		try {
			setApplySubmitting(true);
			const leaveType = values.leave_type;
			const basePayload = {
				applicant_id: values.applicant_id,
				leave_type: leaveType,
				reason: values.reason || null,
			};
			if (APPROVAL_APPLY_TYPES.has(leaveType)) {
				basePayload.approval_received_orders =
					applyApprovalMode === "partial"
						? applyApprovalReceived
						: [];
			}
			let payload = basePayload;
			let successMessage = "휴가 신청이 등록되었습니다.";

			if (leaveType === "사유서") {
				const dates = (
					values.leave_dates || []
				).slice();
				if (!dates.length) {
					message.error(
						"사유서 대상 날짜를 하나 이상 선택해주세요.",
					);
					return;
				}
				payload = {
					...basePayload,
					reason_category: values.reason_category,
					additional_users:
						values.additional_users || [],
					leave_dates: sortDayjsDates(dates).map(
						(d) => d.format("YYYY-MM-DD"),
					),
				};
				successMessage = "사유서가 등록되었습니다.";
			} else if (leaveType === "출장") {
				const start =
					values.start_date.format("YYYY-MM-DD");
				payload = {
					...basePayload,
					start_date: start,
					end_date: values.end_date.format(
						"YYYY-MM-DD",
					),
					leave_date: start,
					trip_title: values.trip_title,
					trip_background: values.trip_background,
					trip_location: values.trip_location,
					trip_includes_lodging: Boolean(
						values.trip_includes_lodging,
					),
					trip_transport: values.trip_transport,
					trip_note: values.trip_note || "",
					additional_users:
						values.additional_users || [],
				};
				successMessage = "출장 일정이 등록되었습니다.";
			} else if (leaveType === "시내출장") {
				payload = {
					...basePayload,
					leave_date: values.leave_date.format(
						"YYYY-MM-DD",
					),
					trip_location: values.trip_location,
					local_trip_departure_time:
						values.local_trip_departure_time.format(
							"HH:mm:ss",
						),
					local_trip_return_type:
						values.local_trip_return_type,
					local_trip_return_time:
						values.local_trip_return_type ===
							"직접 선택" &&
						values.local_trip_return_time
							? values.local_trip_return_time.format(
									"HH:mm:ss",
								)
							: null,
					additional_users:
						values.additional_users || [],
				};
				successMessage = "시내출장이 등록되었습니다.";
			} else if (leaveType === "연차") {
				const dates = (
					values.leave_dates || []
				).slice();
				if (!dates.length) {
					message.error(
						"연차 사용일을 하나 이상 선택해주세요.",
					);
					return;
				}
				payload = {
					...basePayload,
					leave_dates: sortDayjsDates(dates).map(
						(d) => d.format("YYYY-MM-DD"),
					),
				};
				successMessage = "연차가 등록되었습니다.";
			} else if (APPLY_PERIOD_TYPES.includes(leaveType)) {
				const start =
					values.start_date.format("YYYY-MM-DD");
				payload = {
					...basePayload,
					start_date: start,
					end_date: values.end_date.format(
						"YYYY-MM-DD",
					),
					leave_date: start,
				};
				successMessage = "휴가 일정이 등록되었습니다.";
			} else {
				payload = {
					...basePayload,
					leave_date: values.leave_date.format(
						"YYYY-MM-DD",
					),
				};
				if (
					(leaveType === "오전반차" ||
						leaveType === "오후반차") &&
					applyUseHolidayHalf
				) {
					payload.use_holiday_half_day = true;
				}
			}

			const response = await useAxios.post(
				"/utils/leave/apply/",
				payload,
			);
			if (response.status === 201) {
				message.success(successMessage);
				setApplyModalOpen(false);
				const nextTab = tabForApplyLeaveType(leaveType);
				if (nextTab) {
					setActiveTab(nextTab);
				}
				fetchApplications();
			}
		} catch (error) {
			message.error(
				firstApiErrorMessage(error.response?.data) ||
					"신청 처리 중 오류가 발생했습니다.",
			);
			console.error(error);
		} finally {
			setApplySubmitting(false);
		}
	};

	const editDisabledDate = (current) => {
		if (current && current.day() === 0) {
			return true;
		}
		if (
			editLeaveType === "사유서" ||
			editLeaveType === "월차" ||
			editLeaveType === "생휴" ||
			isDateRangeLeaveType(editLeaveType)
		) {
			return false;
		}
		return current && current < dayjs().startOf("day");
	};

	const handleEditLeaveTypeChange = (value) => {
		setEditLeaveType(value);
		if (value === "사유서") {
			editForm.setFieldsValue({
				leave_date: undefined,
				start_date: undefined,
				end_date: undefined,
			});
			return;
		}
		if (value === "출장") {
			editForm.setFieldsValue({
				leave_date: undefined,
				reason: undefined,
				start_date: undefined,
				end_date: undefined,
			});
			return;
		}
		if (ADMIN_SUBTAB_VACATION_TYPES.includes(value)) {
			editForm.setFieldsValue({
				leave_date: undefined,
				reason: undefined,
				start_date: undefined,
				end_date: undefined,
			});
			return;
		}
		editForm.setFieldsValue({
			reason: undefined,
			additional_users: undefined,
			start_date: undefined,
			end_date: undefined,
		});
		setSelectedAdditionalUsers([]);
	};

	const openEditModal = (record) => {
		setAdditionalUsersContext("edit");
		setEditingRecord(record);
		editForm.resetFields();
		setSearchText("");
		const extra = (record.additional_users_info || []).map((u) => ({
			id: u.id,
			name: u.name,
			job: u.job,
			gender: u.gender,
		}));
		setSelectedAdditionalUsers(extra);
		setEditLeaveType(record.leave_type);
		const baseValues = {
			leave_type: record.leave_type,
			reason: record.reason || undefined,
			reason_category: record.reason_category || undefined,
			additional_users: extra.map((u) => u.id),
			trip_title: record.trip_title || undefined,
			trip_background: record.trip_background || undefined,
			trip_location: record.trip_location || undefined,
			trip_includes_lodging: Boolean(
				record.trip_includes_lodging,
			),
			trip_transport: record.trip_transport || undefined,
			trip_note: record.trip_note || undefined,
			local_trip_departure_time: parseApiTime(
				record.local_trip_departure_time,
			),
			local_trip_return_type:
				record.local_trip_return_type || undefined,
			local_trip_return_time: parseApiTime(
				record.local_trip_return_time,
			),
		};
		if (usesStartEndDates(record.leave_type)) {
			const sd = record.start_date;
			const ed = record.end_date;
			const hasRange = Boolean(sd) && Boolean(ed);
			const fb = record.leave_date
				? dayjs(record.leave_date)
				: undefined;
			editForm.setFieldsValue({
				...baseValues,
				start_date: hasRange ? dayjs(sd) : fb,
				end_date: hasRange ? dayjs(ed) : fb,
			});
		} else {
			editForm.setFieldsValue({
				...baseValues,
				start_date: undefined,
				end_date: undefined,
				leave_date: record.leave_date
					? dayjs(record.leave_date)
					: undefined,
			});
		}
		setEditModalOpen(true);
		fetchClergyStaff();
	};

	const onEditFinish = async (values) => {
		if (!editingRecord) return;
		try {
			const payload = {
				leave_type: values.leave_type,
				reason: values.reason || null,
				is_applied: false,
			};
			if (
				values.leave_type === "사유서" ||
				values.leave_type === "출장" ||
				values.leave_type === "시내출장"
			) {
				payload.additional_users =
					values.additional_users || [];
			}
			if (values.leave_type === "사유서") {
				payload.reason_category =
					values.reason_category || null;
			}
			if (values.leave_type === "출장") {
				payload.start_date =
					values.start_date.format("YYYY-MM-DD");
				payload.end_date =
					values.end_date.format("YYYY-MM-DD");
				payload.leave_date = payload.start_date;
				payload.trip_title = values.trip_title;
				payload.trip_background =
					values.trip_background;
				payload.trip_location = values.trip_location;
				payload.trip_includes_lodging = Boolean(
					values.trip_includes_lodging,
				);
				payload.trip_transport = values.trip_transport;
				payload.trip_note = values.trip_note || "";
			} else if (values.leave_type === "시내출장") {
				payload.leave_date =
					values.leave_date.format("YYYY-MM-DD");
				payload.trip_location = values.trip_location;
				payload.local_trip_departure_time =
					values.local_trip_departure_time.format(
						"HH:mm:ss",
					);
				payload.local_trip_return_type =
					values.local_trip_return_type;
				payload.local_trip_return_time =
					values.local_trip_return_type ===
						"직접 선택" &&
					values.local_trip_return_time
						? values.local_trip_return_time.format(
								"HH:mm:ss",
							)
						: null;
			} else if (usesStartEndDates(values.leave_type)) {
				payload.start_date =
					values.start_date.format("YYYY-MM-DD");
				payload.end_date =
					values.end_date.format("YYYY-MM-DD");
				payload.leave_date = payload.start_date;
			} else {
				payload.leave_date =
					values.leave_date.format("YYYY-MM-DD");
			}
			const response = await useAxios.put(
				`/utils/leave/applications/${editingRecord.id}/`,
				payload,
			);
			if (response.status === 200) {
				message.success("수정되었습니다.");
				setEditModalOpen(false);
				setEditingRecord(null);
				fetchApplications();
			}
		} catch (error) {
			message.error(
				firstApiErrorMessage(error.response?.data) ||
					"수정 중 오류가 발생했습니다.",
			);
			console.error(error);
		}
	};

	const fetchAvailableUsers = async () => {
		try {
			const response = await useAxios.get(
				"/users/clergy-staff/",
			);
			if (response.status === 200) {
				setAvailableUsers(response.data);
				setFilteredUsers(response.data);
			}
		} catch (error) {
			message.error("사용자 목록을 불러오는데 실패했습니다.");
			console.error(error);
		}
	};

	useEffect(() => {
		if (additionalUsersModalVisible) {
			fetchAvailableUsers();
		}
	}, [additionalUsersModalVisible]);

	useEffect(() => {
		if (!searchText) {
			setFilteredUsers(availableUsers);
			return;
		}
		const filtered = availableUsers.filter(
			(user) =>
				user.name
					.toLowerCase()
					.includes(searchText.toLowerCase()) ||
				(user.classification_name &&
					user.classification_name
						.toLowerCase()
						.includes(
							searchText.toLowerCase(),
						)),
		);
		setFilteredUsers(filtered);
	}, [searchText, availableUsers]);

	const additionalUsersColumns = [
		{
			title: "이름",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "직분",
			dataIndex: "classification_name",
			key: "classification_name",
		},
	];

	const additionalRowSelection = {
		selectedRowKeys: selectedAdditionalUsers.map((u) => u.id),
		preserveSelectedRowKeys: true,
		onChange: (selectedRowKeys) => {
			setSelectedAdditionalUsers((prev) => {
				const byId = new Map(
					availableUsers.map((u) => [u.id, u]),
				);
				return selectedRowKeys
					.map(
						(id) =>
							byId.get(id) ??
							prev.find(
								(u) =>
									u.id ===
									id,
							),
					)
					.filter(Boolean);
			});
		},
	};

	const openCancelModal = (record) => {
		setCancellingRecord(record);
		cancelForm.resetFields();
		setCancelModalOpen(true);
	};

	const APPROVAL_STATUS_OPTIONS = [
		{ value: "pending", label: "대기" },
		{ value: "approved", label: "승인" },
		{ value: "rejected", label: "반려" },
		{ value: "not_required", label: "불필요" },
	];

	const openApprovalEditModal = (record) => {
		setApprovalEditRecord(record);
		const steps = record.approval_steps || [];
		if (steps.length) {
			approvalEditForm.setFieldsValue({
				steps: steps.map((s) => ({
					id: s.id,
					status: s.status,
					label: s.label || `${s.order}단계`,
					approver_name: s.approver_name,
				})),
			});
		} else {
			approvalEditForm.setFieldsValue({
				team_leader_status: record.team_leader_status,
				office_director_status:
					record.office_director_status,
				church_leader_status:
					record.church_leader_status,
			});
		}
		setApprovalEditOpen(true);
	};

	const submitApprovalEdit = async () => {
		if (!approvalEditRecord) return;
		try {
			const values = await approvalEditForm.validateFields();
			setApprovalEditSaving(true);
			const steps = approvalEditRecord.approval_steps || [];
			const payload = steps.length
				? {
						steps: (values.steps || []).map(
							(s) => ({
								id: s.id,
								status: s.status,
							}),
						),
					}
				: {
						team_leader_status:
							values.team_leader_status,
						office_director_status:
							values.office_director_status,
						church_leader_status:
							values.church_leader_status,
					};
			await useAxios.post(
				`/utils/leave/approvals/${approvalEditRecord.id}/admin-edit/`,
				payload,
			);
			message.success("결재 상태를 수정했습니다.");
			setApprovalEditOpen(false);
			setApprovalEditRecord(null);
			fetchApplications();
		} catch (error) {
			if (error?.errorFields) return;
			message.error(
				error?.response?.data?.error ||
					"결재 상태 수정에 실패했습니다.",
			);
		} finally {
			setApprovalEditSaving(false);
		}
	};

	const unappliedCount = (list) =>
		(list || []).filter(
			(app) => !app.is_cancelled && !app.is_applied,
		).length;

	const tabLabelWithBadge = (label, list) => {
		const count = unappliedCount(list);
		if (!count) return label;
		return (
			<span>
				{label}{" "}
				<Badge
					count={count}
					overflowCount={99}
					size="small"
					style={{ marginLeft: 4 }}
				/>
			</span>
		);
	};

	const submitCancelWithReason = async () => {
		try {
			const values = await cancelForm.validateFields();
			if (!cancellingRecord) return;
			setLoading(true);
			const response = await useAxios.post(
				`/utils/leave/applications/${cancellingRecord.id}/cancel/`,
				{
					cancellation_reason:
						values.cancellation_reason.trim(),
				},
			);

			if (response.status === 200) {
				message.success("휴가 신청이 취소되었습니다.");
				setCancelModalOpen(false);
				setCancellingRecord(null);
				cancelForm.resetFields();
				fetchApplications();
			}
		} catch (error) {
			if (error?.errorFields) {
				return Promise.reject();
			}
			message.error(
				error.response?.data?.error ||
					"취소 중 오류가 발생했습니다.",
			);
			console.error("에러:", error);
			return Promise.reject();
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteRecord = (record) => {
		Modal.confirm({
			title: "이 항목을 목록에서 숨깁니다",
			content: "목록과 본인 내역에서 더 이상 보이지 않으며, 복구할 수 없습니다. 계속하시겠습니까?",
			okText: "숨기기",
			okType: "danger",
			cancelText: "닫기",
			onOk: async () => {
				try {
					setLoading(true);
					const response = await useAxios.delete(
						`/utils/leave/applications/${record.id}/`,
					);
					if (
						response.status === 204 ||
						response.status === 200
					) {
						message.success(
							"목록에서 숨겼습니다.",
						);
						fetchApplications();
					}
				} catch (error) {
					message.error(
						error.response?.data?.error ||
							"처리 중 오류가 발생했습니다.",
					);
					console.error(error);
				} finally {
					setLoading(false);
				}
			},
		});
	};

	const getJobLabel = (job, gender) => {
		if (job === "목사") {
			return "목사";
		} else if (job === "전도사") {
			return gender === "남" ? "남전도사" : "여전도사";
		} else if (job === "교직원") {
			return gender === "남" ? "남직원" : "여직원";
		}
		return job || "-";
	};

	const getJobFilterValue = (job, gender) => {
		if (job === "목사") {
			return "목사";
		} else if (job === "전도사") {
			return gender === "남" ? "남전도사" : "여전도사";
		} else if (job === "교직원") {
			return gender === "남" ? "남직원" : "여직원";
		}
		return null;
	};

	const downloadProofAttachment = async (applicationId, attachment) => {
		try {
			const url = await fetchProofAttachmentBlobUrl(
				applicationId,
				attachment,
			);
			const a = document.createElement("a");
			a.href = url;
			a.download =
				attachment.original_filename || "download";
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			message.error("증빙자료 다운로드에 실패했습니다.");
			console.error(error);
		}
	};

	const handleProofUpload = async (record, file) => {
		const formData = new FormData();
		formData.append("file", file);
		try {
			const response = await useAxios.post(
				`/utils/leave/applications/${record.id}/proof-attachments/`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				},
			);
			if (
				response.status === 200 ||
				response.status === 201
			) {
				message.success("증빙자료가 첨부되었습니다.");
				fetchApplications();
			}
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"증빙자료 업로드에 실패했습니다.",
			);
		}
		return false;
	};

	const handleProofDelete = (record, attachment) => {
		Modal.confirm({
			title: "증빙자료 삭제",
			content: `"${attachment.original_filename}" 파일을 삭제하시겠습니까?`,
			okText: "삭제",
			okType: "danger",
			cancelText: "취소",
			onOk: async () => {
				try {
					await useAxios.delete(
						`/utils/leave/applications/${record.id}/proof-attachments/${attachment.id}/`,
					);
					message.success("증빙자료가 삭제되었습니다.");
					fetchApplications();
				} catch (error) {
					message.error(
						error.response?.data?.error ||
							"증빙자료 삭제에 실패했습니다.",
					);
				}
			},
		});
	};

	const getColumns = (leaveTypes, options = {}) => {
		const {
			constrainReason = false,
			reasonWidth = 260,
			hideAdditionalUsers = false,
			showProofAttachments = false,
		} = options;
		const showReasonCategory =
			Array.isArray(leaveTypes) &&
			leaveTypes.length === 1 &&
			leaveTypes[0] === "사유서";
		return [
			{
				title: "신청자",
				key: "applicant",
				width: 120,
				render: (_, record) => (
					<div>
						<div className="cell-applicant__name">
							{record.user_name}
						</div>
						<div className="cell-applicant__meta">
							{getJobLabel(
								record.user_job,
								record.user_gender,
							)}
						</div>
					</div>
				),
			},
			{
				title: "휴가 구분",
				dataIndex: "leave_type",
				key: "leave_type",
				width: 140,
				render: (type, record) => {
					const colorMap = {
						월차: "blue",
						생휴: "pink",
						사유서: "orange",
						출장: "green",
						연차: "geekblue",
						경조사: "purple",
						병가: "magenta",
						특별휴가: "gold",
						기타: "lime",
						오전반차: "cyan",
						오후반차: "cyan",
					};
					return (
						<Space size={4} wrap>
							<Tag
								color={
									colorMap[
										type
									] ||
									"default"
								}>
								{type}
							</Tag>
							{record.uses_holiday_half_day ? (
								<Tag color="purple">
									공휴일
									반차
								</Tag>
							) : null}
						</Space>
					);
				},
			},
			{
				title: "사용일",
				key: "leave_date",
				width: 210,
				onCell: () => ({
					style: {
						whiteSpace: "nowrap",
					},
				}),
				render: (value, record) => (
					<div>
						<div className="cell-date__main">
							{renderLeavePeriodCell(
								value,
								record,
							)}
						</div>
						<div className="cell-date__sub">
							신청{" "}
							{dayjs(
								record.application_date,
							).format("YYYY-MM-DD")}
						</div>
					</div>
				),
			},
			...(showReasonCategory
				? [
						{
							title: "세부분류",
							dataIndex: "reason_category",
							key: "reason_category",
							width: 110,
							render: (cat) =>
								cat || "—",
						},
					]
				: []),
			{
				title: "사유",
				dataIndex: "reason",
				key: "reason",
				...(constrainReason
					? {
							width: reasonWidth,
							ellipsis: {
								showTitle: false,
							},
						}
					: {}),
				render: (reason) => {
					const text =
						(reason &&
							String(
								reason,
							).trim()) ||
						"-";
					if (!constrainReason) {
						return text;
					}
					return (
						<Tooltip
							title={
								<div
									style={{
										whiteSpace: "pre-wrap",
									}}>
									{text}
								</div>
							}
							placement="topLeft"
							overlayStyle={{
								maxWidth: 520,
							}}>
							<span
								style={{
									display: "block",
									maxWidth: "100%",
									overflow: "hidden",
									textOverflow:
										"ellipsis",
									whiteSpace: "nowrap",
								}}>
								{text}
							</span>
						</Tooltip>
					);
				},
			},
			...(hideAdditionalUsers
				? []
				: [
						{
							title: "추가 해당인",
							dataIndex: "additional_users_info",
							key: "additional_users_info",
							width: 200,
							onCell: () => ({
								style: {
									maxWidth: 200,
									overflow: "hidden",
								},
							}),
							render: (users) => {
								if (
									!users ||
									users.length ===
										0
								) {
									return "-";
								}
								const line =
									users
										.map(
											(
												u,
											) =>
												`${u.name} (${getJobLabel(
													u.job,
													u.gender ||
														null,
												)})`,
										)
										.join(
											", ",
										);
								const detail = (
									<div>
										<div
											style={{
												marginBottom: 8,
												fontWeight: 600,
											}}>
											총{" "}
											{
												users.length
											}

											명
										</div>
										{users.map(
											(
												user,
												index,
											) => (
												<div
													key={
														index
													}
													style={{
														marginBottom: 4,
													}}>
													{
														user.name
													}{" "}
													(
													{getJobLabel(
														user.job,
														user.gender ||
															null,
													)}

													)
												</div>
											),
										)}
									</div>
								);
								return (
									<Tooltip
										title={
											detail
										}
										placement="topLeft">
										<span
											style={{
												display: "block",
												width: "100%",
												maxWidth: "100%",
												overflow: "hidden",
												textOverflow:
													"ellipsis",
												whiteSpace: "nowrap",
											}}>
											{
												line
											}
										</span>
									</Tooltip>
								);
							},
						},
					]),
			...(showProofAttachments
				? [
						{
							title: "증빙자료",
							key: "proof_attachments",
							width: 220,
							render: (_, record) => {
								const files =
									record.proof_attachments ||
									[];
								return (
									<Space
										direction="vertical"
										size={4}
										style={{
											width: "100%",
										}}>
										{files.map(
											(
												att,
											) => (
												<Space
													key={
														att.id
													}
													size={4}
													wrap>
													<span
														style={{
															display:
																"inline-block",
															maxWidth: 100,
															overflow:
																"hidden",
															textOverflow:
																"ellipsis",
															whiteSpace:
																"nowrap",
															verticalAlign:
																"middle",
															fontSize: 12,
														}}
														title={
															att.original_filename
														}>
														{att.original_filename ||
															"파일"}
													</span>
													{isViewableProofAttachment(
														att.original_filename,
													) ? (
														<Button
															type="link"
															size="small"
															icon={
																<EyeOutlined />
															}
															onClick={() =>
																openProofPreview(
																	record.id,
																	att,
																)
															}>
															보기
														</Button>
													) : null}
													<Button
														type="link"
														size="small"
														icon={
															<DownloadOutlined />
														}
														onClick={() =>
															downloadProofAttachment(
																record.id,
																att,
															)
														}
													/>
													<Button
														type="text"
														size="small"
														danger
														icon={
															<DeleteOutlined />
														}
														onClick={() =>
															handleProofDelete(
																record,
																att,
															)
														}
													/>
												</Space>
											),
										)}
										<Upload
											showUploadList={
												false
											}
											beforeUpload={(
												file,
											) => {
												handleProofUpload(
													record,
													file,
												);
												return false;
											}}>
											<Button
												size="small"
												icon={
													<UploadOutlined />
												}>
												첨부
											</Button>
										</Upload>
									</Space>
								);
							},
						},
					]
				: []),
			{
				title: "상태",
				key: "status",
				width: 70,
				render: (_, record) => {
					if (record.is_cancelled) {
						const reason =
							record.cancellation_reason &&
							String(
								record.cancellation_reason,
							).trim();
						return (
							<Tooltip
								title={
									reason ||
									"등록된 취소 사유가 없습니다."
								}>
								<span>
									<Tag color="red">
										취소됨
									</Tag>
								</span>
							</Tooltip>
						);
					}
					if (!REPORT_ONLY_TYPES.has(record.leave_type)) {
						const state = deriveApprovalState(record);
						return (
							<span
								className={`status-pill status-pill--${state}`}>
								{approvalStateLabel(state)}
							</span>
						);
					}
					return record.is_applied ? (
						<Tag color="green">적용</Tag>
					) : (
						<Tag color="default">
							미적용
						</Tag>
					);
				},
			},
			{
				title: "적용",
				dataIndex: "is_applied",
				key: "is_applied",
				width: 100,
				render: (isApplied, record) => {
					if (record.is_cancelled) {
						return (
							<Tag color="red">
								취소됨
							</Tag>
						);
					}
					return (
						<Switch
							checked={isApplied}
							loading={
								togglingAppliedId ===
								record.id
							}
							onChange={(checked) =>
								handleToggleApplied(
									record,
									checked,
								)
							}
							checkedChildren="적용"
							unCheckedChildren="미적용"
						/>
					);
				},
			},
			{
				title: "관리",
				key: "action",
				width: isLeaveManageCompact ? 76 : 190,
				fixed: "right",
				onCell: () =>
					isLeaveManageCompact
						? {
								style: {
									verticalAlign:
										"top",
									paddingTop: 8,
									paddingBottom: 8,
								},
							}
						: {},
				render: (_, record) => {
					const menuItems = [
						...(!record.is_cancelled
							? [
									{
										key: "cancel",
										label: "취소",
										onClick: () =>
											openCancelModal(
												record,
											),
									},
								]
							: []),
						{
							key: "delete",
							label: "삭제",
							danger: true,
							onClick: () =>
								handleDeleteRecord(
									record,
								),
						},
					];
					return (
						<Space
							direction={
								isLeaveManageCompact
									? "vertical"
									: "horizontal"
							}
							size={
								isLeaveManageCompact
									? 6
									: "small"
							}
							style={
								isLeaveManageCompact
									? {
											width: "100%",
										}
									: undefined
							}>
							{!record.is_cancelled && (
								<>
									<Button
										size="small"
										block={
											isLeaveManageCompact
										}
										onClick={() =>
											openEditModal(
												record,
											)
										}>
										수정
									</Button>
									<Button
										size="small"
										block={
											isLeaveManageCompact
										}
										onClick={() =>
											openApprovalEditModal(
												record,
											)
										}>
										결재 수정
									</Button>
								</>
							)}
							<Dropdown
								menu={{ items: menuItems }}
								trigger={["click"]}
								placement="bottomRight">
								<Button
									size="small"
									block={
										isLeaveManageCompact
									}
									icon={<MoreOutlined />}
									aria-label="더보기"
								/>
							</Dropdown>
						</Space>
					);
				},
			},
		];
	};

	/** 직분(구분) + 이름 검색만 적용 (결재 상태 필터 제외) */
	const getBaseFilteredApplications = (apps) => {
		return apps.filter((app) => {
			if (jobFilter !== "전체") {
				const appJobValue = getJobFilterValue(
					app.user_job,
					app.user_gender,
				);
				if (appJobValue !== jobFilter) {
					return false;
				}
			}
			const query = nameSearch.trim().toLowerCase();
			if (query) {
				const userName = (
					app.user_name || ""
				).toLowerCase();
				if (!userName.includes(query)) {
					return false;
				}
			}
			return true;
		});
	};

	const matchesApprovalStatusFilter = (app) => {
		if (approvalStatusFilter === "all") {
			return true;
		}
		const state = deriveApprovalState(app);
		if (approvalStatusFilter === "pending") {
			return state === "pending" || state === "progress";
		}
		if (approvalStatusFilter === "done") {
			return state === "done";
		}
		if (approvalStatusFilter === "rejected") {
			return state === "rejected";
		}
		return true;
	};

	/** 직분 + 이름 + 결재 상태 필터 모두 적용 */
	const getFilteredApplications = (apps) =>
		getBaseFilteredApplications(apps).filter(
			matchesApprovalStatusFilter,
		);

	const annualHalfBase = applications.filter((app) =>
		ANNUAL_HALF_TYPES.includes(app.leave_type),
	);
	const generalLeaveBase = applications.filter(
		(app) => app.leave_type === "사유서",
	);
	const businessTripBase = applications.filter((app) =>
		TRIP_TYPES.includes(app.leave_type),
	);
	const familyLeaveBase = applications.filter((app) =>
		FAMILY_LEAVE_TYPES.includes(app.leave_type),
	);
	const sickLeaveBase = applications.filter((app) =>
		SICK_LEAVE_TYPES.includes(app.leave_type),
	);
	const menstrualLeaveBase = applications.filter((app) =>
		MENSTRUAL_TYPES.includes(app.leave_type),
	);

	const annualHalfApplications =
		getFilteredApplications(annualHalfBase);
	const generalLeaveApplications =
		getFilteredApplications(generalLeaveBase);
	const businessTripApplications =
		getFilteredApplications(businessTripBase);
	const familyLeaveApplications =
		getFilteredApplications(familyLeaveBase);
	const sickLeaveApplications =
		getFilteredApplications(sickLeaveBase);
	const menstrualLeaveApplications =
		getFilteredApplications(menstrualLeaveBase);

	// Segmented 카운트: 현재 탭의 직분+이름 필터 결과 기준 (결재 상태 필터 제외)
	const activeTabBaseApplications = (() => {
		switch (activeTab) {
			case "사유서":
				return generalLeaveBase;
			case "출장":
				return businessTripBase;
			case "경조휴가":
				return familyLeaveBase;
			case "병가":
				return sickLeaveBase;
			case "생리휴가":
				return menstrualLeaveBase;
			case "연차(반차)":
			default:
				return annualHalfBase;
		}
	})();
	const activeTabCountSource = getBaseFilteredApplications(
		activeTabBaseApplications,
	);
	const approvalCounts = activeTabCountSource.reduce(
		(acc, app) => {
			const state = deriveApprovalState(app);
			if (state === "pending" || state === "progress") {
				acc.pending += 1;
			} else if (state === "done") {
				acc.done += 1;
			} else if (state === "rejected") {
				acc.rejected += 1;
			}
			return acc;
		},
		{ pending: 0, done: 0, rejected: 0 },
	);

	const getRowClassName = (record) => {
		if (REPORT_ONLY_TYPES.has(record.leave_type)) {
			return "";
		}
		const s = deriveApprovalState(record);
		return s === "pending" || s === "progress"
			? "row--pending"
			: "";
	};

	const approvalStatusCol = {
		title: "결재 현황",
		key: "approval",
		width: 150,
		render: (_, record) => {
			if (REPORT_ONLY_TYPES.has(record.leave_type)) {
				return null;
			}
			return <ApprovalSteps record={record} />;
		},
	};

	const formatTripCompanions = (record) => {
		const users = record.additional_users_info || [];
		if (!users.length) return "—";
		return users
			.map(
				(u) =>
					`${u.name} (${getJobLabel(u.job, u.gender || null)})`,
			)
			.join(", ");
	};

	const renderTripExpandedRow = (record) => {
		const period = renderLeavePeriodCell(null, record);
		if (record.leave_type === "시내출장") {
			return (
				<div className="leave-trip-expanded">
					<div>
						<strong>출장 일자</strong>{" "}
						{period}
					</div>
					<div>
						<strong>목적</strong>{" "}
						{record.reason || "—"}
					</div>
					<div>
						<strong>행선지</strong>{" "}
						{record.trip_location || "—"}
					</div>
					<div>
						<strong>출발 시각</strong>{" "}
						{record.local_trip_departure_time
							? String(
									record.local_trip_departure_time,
								).slice(0, 5)
							: "—"}
					</div>
					<div>
						<strong>복귀</strong>{" "}
						{record.local_trip_return_type ||
							"—"}
						{record.local_trip_return_time
							? ` · ${String(record.local_trip_return_time).slice(0, 5)}`
							: ""}
					</div>
					<div>
						<strong>동행인</strong>{" "}
						{formatTripCompanions(record)}
					</div>
				</div>
			);
		}
		return (
			<div className="leave-trip-expanded">
				<div>
					<strong>제목</strong>{" "}
					{record.trip_title || "—"}
				</div>
				<div>
					<strong>목적</strong>{" "}
					{record.reason || "—"}
				</div>
				<div>
					<strong>배경</strong>{" "}
					{record.trip_background || "—"}
				</div>
				<div>
					<strong>기간</strong> {period}
				</div>
				<div>
					<strong>지역</strong>{" "}
					{record.trip_location || "—"}
				</div>
				<div>
					<strong>숙박</strong>{" "}
					{record.trip_includes_lodging
						? "포함"
						: "미포함"}
				</div>
				<div>
					<strong>교통</strong>{" "}
					{record.trip_transport || "—"}
				</div>
				<div>
					<strong>비고</strong>{" "}
					{record.trip_note || "—"}
				</div>
				<div>
					<strong>동행인</strong>{" "}
					{formatTripCompanions(record)}
				</div>
			</div>
		);
	};

	const rowSelection = {
		selectedRowKeys,
		onChange: (selectedKeys) => {
			setSelectedRowKeys(selectedKeys);
		},
		getCheckboxProps: (record) => ({
			disabled: record.is_cancelled,
		}),
	};

	const withApprovalColumns = (types, options = {}) => {
		const cols = getColumns(types, options);
		// 상태 / 적용 / 관리 앞에 결재 현황 삽입
		const insertAt = Math.max(cols.length - 3, 0);
		return [
			...cols.slice(0, insertAt),
			approvalStatusCol,
			...cols.slice(insertAt),
		];
	};

	const tabItems = [
		{
			key: "연차(반차)",
			label: tabLabelWithBadge(
				"연차(반차)",
				annualHalfApplications,
			),
			children: (
				<Table
					{...leaveAdminTableProps}
					className="data-table"
					rowClassName={getRowClassName}
					rowSelection={rowSelection}
					columns={withApprovalColumns(
						ANNUAL_HALF_TYPES,
						{
							constrainReason: true,
							reasonWidth: 480,
							hideAdditionalUsers: true,
						},
					)}
					dataSource={annualHalfApplications}
					rowKey="id"
					loading={loading}
					pagination={{
						current: monthlyPage,
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) =>
							`총 ${total}건`,
						onChange: (page) =>
							setMonthlyPage(page),
					}}
				/>
			),
		},
		{
			key: "사유서",
			label: tabLabelWithBadge(
				"사유서",
				generalLeaveApplications,
			),
			children: (
				<Table
					{...leaveAdminTableProps}
					className="data-table"
					rowClassName={getRowClassName}
					rowSelection={rowSelection}
					columns={getColumns(["사유서"], {
						constrainReason: true,
					})}
					dataSource={generalLeaveApplications}
					rowKey="id"
					loading={loading}
					pagination={{
						current: generalPage,
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) =>
							`총 ${total}건`,
						onChange: (page) =>
							setGeneralPage(page),
					}}
				/>
			),
		},
		{
			key: "출장",
			label: tabLabelWithBadge(
				"출장(시내출장 포함)",
				businessTripApplications,
			),
			children: (
				<Table
					{...leaveAdminTableProps}
					className="data-table"
					rowClassName={getRowClassName}
					rowSelection={rowSelection}
					columns={withApprovalColumns(
						TRIP_TYPES,
						{
							constrainReason: true,
						},
					)}
					dataSource={businessTripApplications}
					rowKey="id"
					loading={loading}
					expandable={{
						expandedRowRender:
							renderTripExpandedRow,
						expandRowByClick: true,
						rowExpandable: () => true,
					}}
					pagination={{
						current: tripPage,
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) =>
							`총 ${total}건`,
						onChange: (page) =>
							setTripPage(page),
					}}
				/>
			),
		},
		{
			key: "경조휴가",
			label: tabLabelWithBadge(
				"경조휴가",
				familyLeaveApplications,
			),
			children: (
				<Table
					{...leaveAdminTableProps}
					className="data-table"
					rowClassName={getRowClassName}
					rowSelection={rowSelection}
					columns={withApprovalColumns(
						FAMILY_LEAVE_TYPES,
						{
							showProofAttachments: true,
						},
					)}
					dataSource={familyLeaveApplications}
					rowKey="id"
					loading={loading}
					pagination={{
						current: familyPage,
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) =>
							`총 ${total}건`,
						onChange: (page) =>
							setFamilyPage(page),
					}}
				/>
			),
		},
		{
			key: "병가",
			label: tabLabelWithBadge("병가", sickLeaveApplications),
			children: (
				<Table
					{...leaveAdminTableProps}
					className="data-table"
					rowClassName={getRowClassName}
					rowSelection={rowSelection}
					columns={withApprovalColumns(
						SICK_LEAVE_TYPES,
						{
							constrainReason: true,
							showProofAttachments: true,
						},
					)}
					dataSource={sickLeaveApplications}
					rowKey="id"
					loading={loading}
					pagination={{
						current: sickPage,
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) =>
							`총 ${total}건`,
						onChange: (page) =>
							setSickPage(page),
					}}
				/>
			),
		},
		{
			key: "생리휴가",
			label: tabLabelWithBadge(
				"생리휴가",
				menstrualLeaveApplications,
			),
			children: (
				<Table
					{...leaveAdminTableProps}
					className="data-table"
					rowClassName={getRowClassName}
					rowSelection={rowSelection}
					columns={getColumns(MENSTRUAL_TYPES)}
					dataSource={menstrualLeaveApplications}
					rowKey="id"
					loading={loading}
					pagination={{
						current: menstrualPage,
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) =>
							`총 ${total}건`,
						onChange: (page) =>
							setMenstrualPage(page),
					}}
				/>
			),
		},
	];

	const _unusedVacationPage = vacationPage;
	void _unusedVacationPage;

	// 탭 변경 시 선택 초기화
	const handleTabChange = (key) => {
		setActiveTab(key);
		setSelectedRowKeys([]);
		setMonthlyPage(1);
		setGeneralPage(1);
		setTripPage(1);
		setVacationPage(1);
	};

	// 필터 변경 시 페이지 초기화
	const handleJobFilterChange = (value) => {
		setJobFilter(value);
		setMonthlyPage(1);
		setGeneralPage(1);
		setTripPage(1);
		setVacationPage(1);
	};

	const handleApprovalStatusFilterChange = (value) => {
		setApprovalStatusFilter(value);
		setMonthlyPage(1);
		setGeneralPage(1);
		setTripPage(1);
		setVacationPage(1);
		setFamilyPage(1);
		setSickPage(1);
		setMenstrualPage(1);
	};

	const handleNameSearchChange = (e) => {
		setNameSearch(e.target.value);
		setMonthlyPage(1);
		setGeneralPage(1);
		setTripPage(1);
		setVacationPage(1);
	};

	const renderApplyDateRange = (startLabel, endLabel) => (
		<>
			<Form.Item
				name="start_date"
				label={startLabel}
				rules={[
					{
						required: true,
						message: "시작일을 선택해주세요",
					},
				]}>
				<DatePicker
					inputReadOnly={isLeaveManageCompact || isMobile}
					style={{ width: "100%" }}
					format="YYYY-MM-DD"
					disabledDate={applyDisabledDate}
				/>
			</Form.Item>
			<Form.Item
				name="end_date"
				label={endLabel}
				dependencies={["start_date"]}
				rules={applyEndDateRules(applyLeaveType)}>
				<DatePicker
					inputReadOnly={isLeaveManageCompact || isMobile}
					style={{ width: "100%" }}
					format="YYYY-MM-DD"
					disabledDate={applyDisabledDate}
				/>
			</Form.Item>
		</>
	);

	const renderAdditionalUsersField = (label) => (
		<Form.Item name="additional_users" label={label}>
			<Space direction="vertical" style={{ width: "100%" }}>
				<Button
					type="dashed"
					onClick={() => {
						setAdditionalUsersContext(
							"apply",
						);
						setAdditionalUsersModalVisible(
							true,
						);
						setSearchText("");
					}}
					block
					icon={<UserOutlined />}>
					{label} 선택
				</Button>
				{selectedAdditionalUsers.length > 0 && (
					<div>
						{selectedAdditionalUsers.map(
							(user) => (
								<Tag
									key={
										user.id
									}
									closable
									onClose={() => {
										const next =
											selectedAdditionalUsers.filter(
												(
													u,
												) =>
													u.id !==
													user.id,
											);
										setSelectedAdditionalUsers(
											next,
										);
										applyForm.setFieldsValue(
											{
												additional_users:
													next.map(
														(
															u,
														) =>
															u.id,
													),
											},
										);
									}}
									style={{
										marginBottom: 8,
									}}>
									{
										user.name
									}{" "}
									(
									{getJobLabel(
										user.job,
										user.gender,
									)}
									)
								</Tag>
							),
						)}
					</div>
				)}
			</Space>
		</Form.Item>
	);

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="휴가 신청 관리" />
			<Layout
				className={
					hideNavigation
						? "attendance-embedded-leave"
						: "member-layout"
				}>
				{hideNavigation ? null : (
					<LeaveManageNavigation />
				)}
				<Layout
					className={
						hideNavigation
							? undefined
							: "member-content-layout"
					}>
					<Content
						className={
							hideNavigation
								? "attendance-embedded-leave__content utils-leave-manage-layout"
								: "member-content utils-leave-manage-layout"
						}>
						<div className="page utils-leave-manage-container">
							<PageHeader
								breadcrumb={[
									{ title: "복무 신청 관리" },
									{ title: "신청 관리" },
								]}
								title="근태 신청 관리"
								actions={
									<>
										<input
											ref={gateDutyInputRef}
											type="file"
											accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
											style={{ display: "none" }}
											onChange={handleGateDutyFile}
										/>
										<Button
											icon={<UploadOutlined />}
											loading={gateDutyUploading}
											onClick={() =>
												gateDutyInputRef.current?.click()
											}>
											{isLeaveManageCompact
												? "방비 업로드"
												: "방비 일정 업로드"}
										</Button>
										<Button
											type="primary"
											icon={<PlusOutlined />}
											onClick={() =>
												openApplyModal(activeTab)
											}>
											{isLeaveManageCompact
												? "신청 등록"
												: "근태 신청 등록"}
										</Button>
									</>
								}
							/>
							<div className="surface-card utils-leave-manage-card">
								<div className="toolbar">
									<div className="toolbar__left">
										<Input
											className="utils-leave-manage-search toolbar__search"
											placeholder="이름으로 검색"
											prefix={<SearchOutlined />}
											value={nameSearch}
											onChange={handleNameSearchChange}
											allowClear
										/>
										<Select
											value={jobFilter}
											onChange={handleJobFilterChange}
											className="utils-leave-manage-toolbar-select"
											popupMatchSelectWidth={false}>
											<Option value="전체">전체</Option>
											<Option value="목사">목사</Option>
											<Option value="남전도사">
												남전도사
											</Option>
											<Option value="여전도사">
												여전도사
											</Option>
											<Option value="남직원">
												남직원
											</Option>
											<Option value="여직원">
												여직원
											</Option>
										</Select>
									</div>
									<div className="toolbar__right">
										<Segmented
											className="status-filter"
											value={approvalStatusFilter}
											onChange={
												handleApprovalStatusFilterChange
											}
											options={[
												{
													label: <span>전체</span>,
													value: "all",
												},
												{
													label: (
														<span>
															미결재{" "}
															<span className="status-filter__count status-filter__count--pending">
																{approvalCounts.pending}
															</span>
														</span>
													),
													value: "pending",
												},
												{
													label: (
														<span>
															승인 완료{" "}
															<span className="status-filter__count status-filter__count--done">
																{approvalCounts.done}
															</span>
														</span>
													),
													value: "done",
												},
												{
													label: (
														<span>
															반려{" "}
															<span className="status-filter__count status-filter__count--rejected">
																{approvalCounts.rejected}
															</span>
														</span>
													),
													value: "rejected",
												},
											]}
										/>
									</div>
								</div>
								{selectedRowKeys.length > 0 && (
									<Space style={{ marginBottom: 12 }} wrap>
										<Button
											type="primary"
											size={
												isLeaveManageCompact
													? "small"
													: "middle"
											}
											onClick={() =>
												handleBulkUpdate(true)
											}>
											선택 항목 적용 (
											{selectedRowKeys.length})
										</Button>
										<Button
											size={
												isLeaveManageCompact
													? "small"
													: "middle"
											}
											onClick={() =>
												handleBulkUpdate(false)
											}>
											선택 항목 미적용 (
											{selectedRowKeys.length})
										</Button>
									</Space>
								)}
								<Tabs
									className="utils-leave-manage-tabs"
									activeKey={activeTab}
									onChange={handleTabChange}
									items={tabItems}
								/>
								<div className="table-footer">
									<span>노란 줄 = 결재 미완료</span>
								</div>
							</div>
							<Modal
								title="근태 신청 등록 (관리자)"
								rootClassName="utils-leave-manage-form-modal"
								open={
									applyModalOpen
								}
								onCancel={() =>
									setApplyModalOpen(
										false,
									)
								}
								footer={null}
								destroyOnClose
								width={600}>
								<Form
									form={
										applyForm
									}
									layout="vertical"
									onFinish={
										handleApplySubmit
									}>
									<Form.Item
										name="applicant_id"
										label="신청자"
										rules={[
											{
												required: true,
												message: "신청자를 선택해주세요",
											},
										]}>
										<Select
											showSearch
											placeholder="이름으로 검색"
											optionFilterProp="children"
											filterOption={(
												input,
												option,
											) =>
												String(
													option?.label ??
														"",
												)
													.toLowerCase()
													.includes(
														input.toLowerCase(),
													)
											}
											onChange={
												handleApplyApplicantChange
											}
											options={clergyStaffList.map(
												(
													u,
												) => ({
													value: u.id,
													label: `${u.name} (${u.classification_name || "-"})`,
												}),
											)}
										/>
									</Form.Item>
									<Form.Item
										name="leave_type"
										label="구분"
										rules={[
											{
												required: true,
												message: "구분을 선택해주세요",
											},
										]}>
										<Select
											placeholder="구분을 선택하세요"
											onChange={
												handleApplyLeaveTypeChange
											}
											options={
												ADMIN_APPLY_LEAVE_OPTIONS
											}
										/>
									</Form.Item>
									{(applyLeaveType ===
										"오전반차" ||
										applyLeaveType ===
											"오후반차") && (
										<Alert
											type="info"
											showIcon
											style={{
												marginBottom: 16,
											}}
											message="반차 출퇴근 안내"
											description={
												applyLeaveType ===
												"오전반차"
													? "당일 오후 1시까지 출근 찍기가 필요합니다. 출근을 찍지 않으면 연차 1일이 차감됩니다."
													: "출근 찍기 후 12시에 퇴근 찍기가 필요합니다. 출근을 찍지 않으면 연차 1일이 차감됩니다."
											}
										/>
									)}
									{applyLeaveType ===
										"경조사" && (
										<Alert
											type="info"
											showIcon
											style={{
												marginBottom: 16,
											}}
											message="경조휴가는 신청 1건당 최대 5일(일요일 제외)까지 사용할 수 있습니다."
										/>
									)}
									{applyLeaveType ===
										"병가" && (
										<Alert
											type="info"
											showIcon
											style={{
												marginBottom: 16,
											}}
											message="장기 병가 사용 시 생활비 지급에 영향이 있을 수 있습니다."
										/>
									)}
									{APPLY_SINGLE_DATE_TYPES.includes(
										applyLeaveType,
									) && (
										<Form.Item
											name="leave_date"
											label="사용일"
											rules={[
												{
													required: true,
													message: "사용일을 선택해주세요",
												},
											]}>
											<DatePicker
												inputReadOnly={isLeaveManageCompact || isMobile}
												style={{
													width: "100%",
												}}
												format="YYYY-MM-DD"
												disabledDate={
													applyDisabledDate
												}
											/>
										</Form.Item>
									)}
									{showApplyHolidayHalf && (
										<Form.Item label="공휴일 반차">
											<div
												style={{
													marginBottom: 8,
												}}>
												남은
												공휴일
												반차{" "}
												<strong>
													{applyApplicantId
														? applyHolidayHalfRemaining
														: "—"}
												</strong>

												개
											</div>
											<Checkbox
												checked={
													applyUseHolidayHalf
												}
												disabled={
													!applyApplicantId ||
													!applyLeaveDate ||
													applyHolidayHalfUsableOnDate <
														1
												}
												onChange={(
													e,
												) =>
													setApplyUseHolidayHalf(
														e
															.target
															.checked,
													)
												}>
												공휴일
												반차
												사용
												{!applyApplicantId
													? " (신청자를 선택하세요)"
													: !applyLeaveDate
														? " (사용일을 선택하세요)"
														: applyHolidayHalfRemaining <
															  1
															? " (잔여 없음)"
															: applyHolidayHalfUsableOnDate <
																  1
																? " (선택한 날짜에 사용 불가)"
																: ""}
											</Checkbox>
										</Form.Item>
									)}
									{applyLeaveType ===
										"연차" && (
										<>
											<Form.Item
												name="leave_dates"
												label="사용일"
												rules={[
													{
														type: "array",
														required: true,
														min: 1,
														message: "사용일을 하나 이상 선택해주세요.",
													},
												]}>
												<LeaveMultiDatePicker
													disabledDate={
														applyDisabledDate
													}
													placeholder="사용일을 선택하세요"
												/>
											</Form.Item>
											<Form.Item
												name="reason"
												label="사유 (선택)">
												<TextArea
													rows={
														3
													}
													placeholder="필요 시 입력하세요"
												/>
											</Form.Item>
										</>
									)}
									{APPLY_PERIOD_TYPES.includes(
										applyLeaveType,
									) &&
										applyLeaveType !==
											"연차" && (
											<>
												{renderApplyDateRange(
													"시작일",
													"종료일",
												)}
												<Form.Item
													name="reason"
													label={
														applyLeaveType ===
														"연차"
															? "사유 (선택)"
															: "휴가 사유"
													}
													rules={
														applyLeaveType ===
														"연차"
															? []
															: [
																	{
																		required: true,
																		message: "사유를 입력해주세요",
																	},
																]
													}>
													<TextArea
														rows={
															3
														}
														placeholder={
															applyLeaveType ===
															"연차"
																? "필요 시 입력하세요"
																: "휴가 사유를 입력하세요"
														}
													/>
												</Form.Item>
											</>
										)}
									{applyLeaveType ===
										"사유서" && (
										<>
											<Form.Item
												name="leave_dates"
												label="대상 날짜"
												rules={[
													{
														type: "array",
														required: true,
														min: 1,
														message: "날짜를 하나 이상 선택해주세요.",
													},
												]}>
												<LeaveMultiDatePicker
													disabledDate={
														applyDisabledDate
													}
												/>
											</Form.Item>
											<Form.Item
												name="reason_category"
												label="세부분류"
												rules={[
													{
														required: true,
														message: "세부분류를 선택해주세요",
													},
												]}>
												<Select placeholder="세부분류를 선택하세요">
													{EXCUSE_REASON_CATEGORIES.map(
														(
															cat,
														) => (
															<Option
																key={
																	cat
																}
																value={
																	cat
																}>
																{
																	cat
																}
															</Option>
														),
													)}
												</Select>
											</Form.Item>
											<Form.Item
												name="reason"
												label="사유"
												rules={[
													{
														required: true,
														message: "사유를 입력해주세요",
													},
												]}>
												<TextArea
													rows={
														4
													}
													placeholder="사유를 입력하세요"
												/>
											</Form.Item>
											{renderAdditionalUsersField(
												"추가 해당인",
											)}
										</>
									)}
									{applyLeaveType ===
										"출장" && (
										<>
											<Form.Item
												name="trip_title"
												label="제목"
												rules={[
													{
														required: true,
														message: "제목을 입력해주세요",
													},
												]}>
												<Input />
											</Form.Item>
											<Form.Item
												name="reason"
												label="목적(업무 내용)">
												<TextArea
													rows={
														3
													}
												/>
											</Form.Item>
											<Form.Item
												name="trip_background"
												label="배경 및 필요성">
												<TextArea
													rows={
														3
													}
												/>
											</Form.Item>
											{renderApplyDateRange(
												"출장 시작일",
												"출장 종료일",
											)}
											<Form.Item
												name="trip_location"
												label="지역/도시명"
												rules={[
													{
														required: true,
														message: "지역/도시명을 입력해주세요",
													},
												]}>
												<Input />
											</Form.Item>
											{renderAdditionalUsersField(
												"동행 인원",
											)}
											<Form.Item
												name="trip_includes_lodging"
												label="숙박포함 여부"
												valuePropName="checked">
												<Switch
													checkedChildren="포함"
													unCheckedChildren="미포함"
												/>
											</Form.Item>
											<Form.Item
												name="trip_transport"
												label="예정 교통편"
												rules={[
													{
														required: true,
														message: "교통편을 선택해주세요",
													},
												]}>
												<Select>
													{[
														"항공",
														"열차",
														"차량",
														"기타",
													].map(
														(
															t,
														) => (
															<Option
																key={
																	t
																}
																value={
																	t
																}>
																{
																	t
																}
															</Option>
														),
													)}
												</Select>
											</Form.Item>
											<Form.Item
												name="trip_note"
												label="비고">
												<TextArea
													rows={
														2
													}
												/>
											</Form.Item>
										</>
									)}
									{applyLeaveType ===
										"시내출장" && (
										<>
											<Form.Item
												name="leave_date"
												label="출장 일자"
												rules={[
													{
														required: true,
														message: "출장 일자를 선택해주세요",
													},
												]}>
												<DatePicker
													inputReadOnly={isLeaveManageCompact || isMobile}
													style={{
														width: "100%",
													}}
													format="YYYY-MM-DD"
													disabledDate={
														applyDisabledDate
													}
												/>
											</Form.Item>
											<Form.Item
												name="reason"
												label="출장 목적"
												rules={[
													{
														required: true,
														message: "출장 목적을 입력해주세요",
													},
												]}>
												<TextArea
													rows={
														3
													}
												/>
											</Form.Item>
											<Form.Item
												name="trip_location"
												label="행선지"
												rules={[
													{
														required: true,
														message: "행선지를 입력해주세요",
													},
												]}>
												<Input placeholder="행선지를 입력하세요" />
											</Form.Item>
											<Form.Item
												name="local_trip_departure_time"
												label="출발 시각"
												rules={[
													{
														required: true,
														message: "출발 시각을 선택해주세요",
													},
												]}>
												<TimePicker
													inputReadOnly={isLeaveManageCompact || isMobile}
													style={{
														width: "100%",
													}}
													format="HH:mm"
												/>
											</Form.Item>
											<Form.Item
												name="local_trip_return_type"
												label="복귀 예정 시각"
												rules={[
													{
														required: true,
														message: "복귀 예정 시각을 선택해주세요",
													},
												]}>
												<Select
													options={[
														{
															value: "1시간 이내",
															label: "1시간 이내",
														},
														{
															value: "2시간 이내",
															label: "2시간 이내",
														},
														{
															value: "직접 선택",
															label: "직접 선택",
														},
													]}
												/>
											</Form.Item>
											{applyLocalReturnType ===
												"직접 선택" && (
												<Form.Item
													name="local_trip_return_time"
													label="복귀 시각"
													rules={[
														{
															required: true,
															message: "복귀 시각을 선택해주세요",
														},
													]}>
													<TimePicker
														inputReadOnly={isLeaveManageCompact || isMobile}
														style={{
															width: "100%",
														}}
														format="HH:mm"
													/>
												</Form.Item>
											)}
											{renderAdditionalUsersField(
												"추가 해당인",
											)}
										</>
									)}
									<ApplyApprovalLineInput
										leaveType={
											applyLeaveType
										}
										applicantId={
											applyApplicantId
										}
										loading={
											applyApprovalLineLoading
										}
										line={
											applyApprovalLine
										}
										mode={
											applyApprovalMode
										}
										receivedOrders={
											applyApprovalReceived
										}
										onModeChange={
											setApplyApprovalMode
										}
										onReceivedChange={
											setApplyApprovalReceived
										}
									/>
									<Form.Item>
										<Space>
											<Button
												type="primary"
												htmlType="submit"
												loading={
													applySubmitting
												}>
												등록
											</Button>
											<Button
												onClick={() =>
													setApplyModalOpen(
														false,
													)
												}>
												닫기
											</Button>
										</Space>
									</Form.Item>
								</Form>
							</Modal>
							<Modal
								title="근태 신청 수정 (관리자)"
								rootClassName="utils-leave-manage-form-modal"
								open={
									editModalOpen
								}
								onCancel={() => {
									setEditModalOpen(
										false,
									);
									setEditingRecord(
										null,
									);
									editForm.resetFields();
									setSelectedAdditionalUsers(
										[],
									);
								}}
								footer={null}
								destroyOnClose
								width={560}>
								{editingRecord && (
									<Form
										form={
											editForm
										}
										layout="vertical"
										onFinish={
											onEditFinish
										}>
										<Form.Item label="신청자">
											<Input
												disabled
												value={
													editingRecord.user_name ||
													""
												}
											/>
										</Form.Item>
										<Form.Item
											name="leave_type"
											label="구분"
											rules={[
												{
													required: true,
													message: "구분을 선택해주세요",
												},
											]}>
											<Select
												placeholder="구분을 선택하세요"
												onChange={
													handleEditLeaveTypeChange
												}>
												<Option value="월차">
													월차
												</Option>
												{editingRecord.user_gender ===
													"여" && (
													<Option value="생휴">
														생리휴가
													</Option>
												)}
												<Option value="사유서">
													사유서
												</Option>
												<Option value="출장">
													출장
												</Option>
												<Option value="시내출장">
													시내출장
												</Option>
												{ADMIN_SUBTAB_VACATION_TYPES.map(
													(
														t,
													) => (
														<Option
															key={
																t
															}
															value={
																t
															}>
															{
																t
															}
														</Option>
													),
												)}
											</Select>
										</Form.Item>
										{!usesStartEndDates(
											editLeaveType,
										) && (
											<Form.Item
												name="leave_date"
												label={
													editLeaveType ===
													"시내출장"
														? "출장 일자"
														: "사용일"
												}
												rules={[
													{
														required: true,
														message: "날짜를 선택해주세요",
													},
												]}>
												<DatePicker
													inputReadOnly={isLeaveManageCompact || isMobile}
													style={{
														width: "100%",
													}}
													format="YYYY-MM-DD"
													disabledDate={
														editDisabledDate
													}
												/>
											</Form.Item>
										)}
										{editLeaveType ===
											"시내출장" && (
											<>
												<Form.Item
													name="reason"
													label="출장 목적"
													rules={[
														{
															required: true,
															message: "출장 목적을 입력해주세요",
														},
													]}>
													<TextArea
														rows={
															3
														}
													/>
												</Form.Item>
												<Form.Item
													name="trip_location"
													label="행선지"
													rules={[
														{
															required: true,
															message: "행선지를 입력해주세요",
														},
													]}>
													<Input placeholder="행선지를 입력하세요" />
												</Form.Item>
												<Form.Item
													name="local_trip_departure_time"
													label="출발 시각"
													rules={[
														{
															required: true,
															message: "출발 시각을 선택해주세요",
														},
													]}>
													<TimePicker
														inputReadOnly={isLeaveManageCompact || isMobile}
														style={{
															width: "100%",
														}}
														format="HH:mm"
													/>
												</Form.Item>
												<Form.Item
													name="local_trip_return_type"
													label="복귀 예정 시각"
													rules={[
														{
															required: true,
															message: "복귀 예정 시각을 선택해주세요",
														},
													]}>
													<Select
														options={[
															{
																value: "1시간 이내",
																label: "1시간 이내",
															},
															{
																value: "2시간 이내",
																label: "2시간 이내",
															},
															{
																value: "직접 선택",
																label: "직접 선택",
															},
														]}
													/>
												</Form.Item>
												{editLocalReturnType ===
													"직접 선택" && (
													<Form.Item
														name="local_trip_return_time"
														label="복귀 시각"
														rules={[
															{
																required: true,
																message: "복귀 시각을 선택해주세요",
															},
														]}>
														<TimePicker
															inputReadOnly={isLeaveManageCompact || isMobile}
															style={{
																width: "100%",
															}}
															format="HH:mm"
														/>
													</Form.Item>
												)}
											</>
										)}
										{usesStartEndDates(
											editLeaveType,
										) && (
											<>
												<Form.Item
													name="start_date"
													label={
														editLeaveType ===
														"출장"
															? "출장 시작일"
															: "시작일"
													}
													rules={[
														{
															required: true,
															message: "시작일을 선택해주세요",
														},
													]}>
													<DatePicker
														inputReadOnly={isLeaveManageCompact || isMobile}
														style={{
															width: "100%",
														}}
														format="YYYY-MM-DD"
														disabledDate={
															editDisabledDate
														}
													/>
												</Form.Item>
												<Form.Item
													name="end_date"
													label={
														editLeaveType ===
														"출장"
															? "출장 종료일"
															: editLeaveType ===
																  "사유서"
																? "마감일"
																: "종료일"
													}
													dependencies={[
														"start_date",
													]}
													rules={[
														{
															required: true,
															message:
																editLeaveType ===
																"사유서"
																	? "마감일을 선택해주세요"
																	: "종료일을 선택해주세요",
														},
														({
															getFieldValue,
														}) => ({
															validator(
																_,
																value,
															) {
																const start =
																	getFieldValue(
																		"start_date",
																	);
																const errMsg =
																	editLeaveType ===
																	"사유서"
																		? "마감일은 시작일 이후여야 합니다."
																		: "종료일은 시작일 이후여야 합니다.";
																if (
																	!value ||
																	!start
																) {
																	return Promise.resolve();
																}
																if (
																	value.isBefore(
																		start,
																		"day",
																	)
																) {
																	return Promise.reject(
																		new Error(
																			errMsg,
																		),
																	);
																}
																return Promise.resolve();
															},
														}),
													]}>
													<DatePicker
														inputReadOnly={isLeaveManageCompact || isMobile}
														style={{
															width: "100%",
														}}
														format="YYYY-MM-DD"
														disabledDate={
															editDisabledDate
														}
													/>
												</Form.Item>
												{editLeaveType ===
													"출장" && (
													<>
														<Form.Item
															name="trip_title"
															label="제목"
															rules={[
																{
																	required: true,
																	message: "제목을 입력해주세요",
																},
															]}>
															<Input />
														</Form.Item>
														<Form.Item
															name="reason"
															label="목적(업무 내용)">
															<TextArea
																rows={
																	3
																}
															/>
														</Form.Item>
														<Form.Item
															name="trip_background"
															label="배경 및 필요성">
															<TextArea
																rows={
																	3
																}
															/>
														</Form.Item>
														<Form.Item
															name="trip_location"
															label="지역/도시명"
															rules={[
																{
																	required: true,
																	message: "지역/도시명을 입력해주세요",
																},
															]}>
															<Input />
														</Form.Item>
														<Form.Item
															name="trip_includes_lodging"
															label="숙박포함 여부"
															valuePropName="checked">
															<Switch
																checkedChildren="포함"
																unCheckedChildren="미포함"
															/>
														</Form.Item>
														<Form.Item
															name="trip_transport"
															label="예정 교통편"
															rules={[
																{
																	required: true,
																	message: "교통편을 선택해주세요",
																},
															]}>
															<Select>
																{[
																	"항공",
																	"열차",
																	"차량",
																	"기타",
																].map(
																	(
																		t,
																	) => (
																		<Option
																			key={
																				t
																			}
																			value={
																				t
																			}>
																			{
																				t
																			}
																		</Option>
																	),
																)}
															</Select>
														</Form.Item>
														<Form.Item
															name="trip_note"
															label="비고">
															<TextArea
																rows={
																	2
																}
															/>
														</Form.Item>
													</>
												)}
												{ADMIN_SUBTAB_VACATION_TYPES.includes(
													editLeaveType,
												) &&
													editLeaveType ===
														"연차" && (
														<Form.Item
															name="reason"
															label="사유 (선택)">
															<TextArea
																rows={
																	3
																}
																placeholder="필요 시 입력하세요"
															/>
														</Form.Item>
													)}
												{ADMIN_SUBTAB_VACATION_TYPES.includes(
													editLeaveType,
												) &&
													editLeaveType !==
														"연차" && (
														<Form.Item
															name="reason"
															label="사유"
															rules={[
																{
																	required: true,
																	message: "사유를 입력해주세요",
																},
															]}>
															<TextArea
																rows={
																	3
																}
															/>
														</Form.Item>
													)}
											</>
										)}
										{editLeaveType ===
											"사유서" && (
											<>
												<Form.Item
													name="reason_category"
													label="세부분류"
													rules={[
														{
															required: true,
															message: "세부분류를 선택해주세요",
														},
													]}>
													<Select placeholder="세부분류를 선택하세요">
														{EXCUSE_REASON_CATEGORIES.map(
															(
																cat,
															) => (
																<Option
																	key={
																		cat
																	}
																	value={
																		cat
																	}>
																	{
																		cat
																	}
																</Option>
															),
														)}
													</Select>
												</Form.Item>
												<Form.Item
													name="reason"
													label="사유"
													rules={[
														{
															required: true,
															message: "사유를 입력해주세요",
														},
													]}>
													<TextArea
														rows={
															4
														}
														placeholder="사유를 입력하세요"
													/>
												</Form.Item>
											</>
										)}
										{(editLeaveType ===
											"사유서" ||
											editLeaveType ===
												"출장" ||
											editLeaveType ===
												"시내출장") && (
											<Form.Item
												name="additional_users"
												label="추가 해당인">
												<Space
													direction="vertical"
													style={{
														width: "100%",
													}}>
													<Button
														type="dashed"
														onClick={() => {
															setAdditionalUsersContext(
																"edit",
															);
															setAdditionalUsersModalVisible(
																true,
															);
															setSearchText(
																"",
															);
														}}
														block
														icon={
															<UserOutlined />
														}>
														추가
														해당인
														선택
													</Button>
													{selectedAdditionalUsers.length >
														0 && (
														<div>
															{selectedAdditionalUsers.map(
																(
																	user,
																) => (
																	<Tag
																		key={
																			user.id
																		}
																		closable
																		onClose={() => {
																			const next =
																				selectedAdditionalUsers.filter(
																					(
																						u,
																					) =>
																						u.id !==
																						user.id,
																				);
																			setSelectedAdditionalUsers(
																				next,
																			);
																			editForm.setFieldsValue(
																				{
																					additional_users:
																						next.map(
																							(
																								u,
																							) =>
																								u.id,
																						),
																				},
																			);
																		}}
																		style={{
																			marginBottom: 8,
																		}}>
																		{
																			user.name
																		}{" "}
																		(
																		{getJobLabel(
																			user.job,
																			user.gender,
																		)}

																		)
																	</Tag>
																),
															)}
														</div>
													)}
												</Space>
											</Form.Item>
										)}
										<Form.Item>
											<Space>
												<Button
													type="primary"
													htmlType="submit">
													저장
												</Button>
												<Button
													onClick={() => {
														setEditModalOpen(
															false,
														);
														setEditingRecord(
															null,
														);
														editForm.resetFields();
														setSelectedAdditionalUsers(
															[],
														);
													}}>
													닫기
												</Button>
											</Space>
										</Form.Item>
									</Form>
								)}
							</Modal>
							<Modal
								title="신청 취소"
								open={
									cancelModalOpen
								}
								onCancel={() => {
									setCancelModalOpen(
										false,
									);
									setCancellingRecord(
										null,
									);
									cancelForm.resetFields();
								}}
								onOk={
									submitCancelWithReason
								}
								okText="취소 확정"
								cancelText="닫기"
								confirmLoading={
									loading
								}
								destroyOnClose>
								<Form
									form={
										cancelForm
									}
									layout="vertical">
									<Form.Item
										name="cancellation_reason"
										label="취소 사유"
										rules={[
											{
												required: true,
												message: "취소 사유를 입력해주세요",
											},
										]}>
										<TextArea
											rows={
												4
											}
											placeholder="취소 사유를 입력하세요"
										/>
									</Form.Item>
								</Form>
							</Modal>
							<Modal
								title={`${approvalEditRecord?.user_name || ""} — 결재 수정`}
								open={
									approvalEditOpen
								}
								onCancel={() => {
									setApprovalEditOpen(
										false,
									);
									setApprovalEditRecord(
										null,
									);
								}}
								onOk={
									submitApprovalEdit
								}
								okText="저장"
								confirmLoading={
									approvalEditSaving
								}
								destroyOnClose>
								<Form
									form={
										approvalEditForm
									}
									layout="vertical">
									{(
										approvalEditRecord?.approval_steps ||
										[]
									)
										.length >
									0 ? (
										<Form.List name="steps">
											{(
												fields,
											) =>
												fields.map(
													(
														field,
													) => (
														<Space
															key={
																field.key
															}
															align="baseline"
															style={{
																display: "flex",
																marginBottom: 8,
															}}>
															<Form.Item
																{...field}
																name={[
																	field.name,
																	"id",
																]}
																hidden>
																<Input />
															</Form.Item>
															<Form.Item
																{...field}
																name={[
																	field.name,
																	"label",
																]}
																label="단계">
																<Input
																	disabled
																/>
															</Form.Item>
															<Form.Item
																{...field}
																name={[
																	field.name,
																	"approver_name",
																]}
																label="결재자">
																<Input
																	disabled
																/>
															</Form.Item>
															<Form.Item
																{...field}
																name={[
																	field.name,
																	"status",
																]}
																label="상태"
																rules={[
																	{
																		required: true,
																	},
																]}>
																<Select
																	options={
																		APPROVAL_STATUS_OPTIONS
																	}
																	style={{
																		width: 120,
																	}}
																/>
															</Form.Item>
														</Space>
													),
												)
											}
										</Form.List>
									) : (
										<>
											<Form.Item
												name="team_leader_status"
												label="팀 리더"
												rules={[
													{
														required: true,
													},
												]}>
												<Select
													options={
														APPROVAL_STATUS_OPTIONS
													}
												/>
											</Form.Item>
											<Form.Item
												name="office_director_status"
												label="사무국장"
												rules={[
													{
														required: true,
													},
												]}>
												<Select
													options={
														APPROVAL_STATUS_OPTIONS
													}
												/>
											</Form.Item>
											<Form.Item
												name="church_leader_status"
												label="당회장"
												rules={[
													{
														required: true,
													},
												]}>
												<Select
													options={
														APPROVAL_STATUS_OPTIONS
													}
												/>
											</Form.Item>
										</>
									)}
								</Form>
							</Modal>
							<Modal
								title="추가 해당인 선택"
								open={
									additionalUsersModalVisible
								}
								onOk={() => {
									const ids =
										selectedAdditionalUsers.map(
											(
												u,
											) =>
												u.id,
										);
									if (
										additionalUsersContext ===
										"edit"
									) {
										editForm.setFieldsValue(
											{
												additional_users:
													ids,
											},
										);
									} else {
										applyForm.setFieldsValue(
											{
												additional_users:
													ids,
											},
										);
									}
									setAdditionalUsersModalVisible(
										false,
									);
								}}
								onCancel={() => {
									setAdditionalUsersModalVisible(
										false,
									);
									setSearchText(
										"",
									);
								}}
								width={isLeaveManageCompact ? "100%" : 720}
								okText="선택 완료"
								cancelText="취소">
								<Space
									direction="vertical"
									style={{
										width: "100%",
									}}
									size="large">
									<Input
										placeholder="이름 또는 직분으로 검색"
										prefix={
											<SearchOutlined />
										}
										value={
											searchText
										}
										onChange={(
											e,
										) =>
											setSearchText(
												e
													.target
													.value,
											)
										}
									/>
									<Table
										rowSelection={
											additionalRowSelection
										}
										columns={
											additionalUsersColumns
										}
										dataSource={
											filteredUsers
										}
										rowKey="id"
										pagination={{
											pageSize: 10,
											showSizeChanger: true,
										}}
									/>
								</Space>
							</Modal>
							<Modal
								title={
									proofPreview?.title ||
									"증빙자료 미리보기"
								}
								open={
									proofPreviewLoading ||
									Boolean(proofPreview)
								}
								onCancel={closeProofPreview}
								footer={null}
								width={
									typeof window !== "undefined"
										? Math.min(
												900,
												window.innerWidth -
													32,
											)
										: 900
								}
								destroyOnClose
								styles={{
									body: {
										padding: proofPreview
											? 12
											: 24,
										maxHeight:
											"min(80vh, 720px)",
										overflow: "auto",
									},
								}}>
								{proofPreviewLoading &&
								!proofPreview ? (
									<div
										style={{
											textAlign: "center",
											padding: 48,
										}}>
										<Spin tip="불러오는 중..." />
									</div>
								) : null}
								{proofPreview?.kind === "pdf" ? (
									<iframe
										title={
											proofPreview.title
										}
										src={proofPreview.url}
										style={{
											width: "100%",
											height: "min(70vh, 640px)",
											border: "none",
										}}
									/>
								) : null}
								{proofPreview?.kind === "image" ? (
									<div
										style={{
											textAlign: "center",
										}}>
										<img
											src={
												proofPreview.url
											}
											alt={
												proofPreview.title
											}
											style={{
												maxWidth: "100%",
												maxHeight:
													"min(70vh, 640px)",
												objectFit:
													"contain",
											}}
										/>
									</div>
								) : null}
							</Modal>
						</div>
					</Content>
				</Layout>
			</Layout>
		</ConfigProvider>
	);
};

export default ViewLeaveApplications;
